import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/pacing.py. Automatic dial pacing adjusts a campaign's
// channel cap against a target abandon rate; every response carries the
// global arm/kill state and the display timezone (render instants with it,
// never with the browser's — same rule as the Workforce reports).

export interface PacingStatus {
  armed: boolean;
  killed: boolean;
  can_kill: boolean;
  timezone: string;
  tick_interval_s: number;
  min_sample: number;
  default_target_pct: number;
  lookback_s: number;
  settle_s: number;
  raise_cooldown_s: number;
  max_step_down_pct: number;
  max_step_up_pct: number;
}

export interface PacingSettings {
  campaign_id: number;
  enabled: boolean;
  target_pct: number;
  min_channels: number;
  max_channels: number;
  current_channel: number;
  campaign_status: string;
  transfer_target: string;
}

export type PacingAction =
  | "hold"
  | "lower"
  | "raise"
  | "clamp"
  | "external"
  | "enabled"
  | "disabled"
  | "settings";

export interface PacingEvent {
  dial_pacing_event_id: number;
  created_at: string;
  actor: string;
  action: PacingAction;
  reason_code: string;
  reason: string;
  old_cap: number | null;
  new_cap: number | null;
  observed_pct: number | null;
  target_pct: number | null;
  margin_pct: number | null;
  connected: number | null;
  abandoned: number | null;
  pending: number | null;
  window_start: string | null;
  window_end: string | null;
  min_cap: number | null;
  max_cap: number | null;
}

export interface TrailingRate {
  label: string;
  entries: number;
  connected: number;
  abandoned: number;
  pct: number | null;
}

export interface CampaignPacing {
  status: PacingStatus;
  settings: PacingSettings;
  latest: PacingEvent | null;
  events: PacingEvent[];
  trailing: TrailingRate[];
}

export interface PacingSettingsInput {
  enabled?: boolean;
  target_pct?: number;
  min_channels?: number;
  max_channels?: number;
}

export async function getPacingStatus(): Promise<PacingStatus> {
  const resp = await api.get<APIResponse<PacingStatus>>("/pacing/status");
  return unwrap(resp.data);
}

export async function setPacingKill(killed: boolean): Promise<PacingStatus> {
  const resp = await api.put<APIResponse<PacingStatus>>("/pacing/kill", { killed });
  return unwrap(resp.data);
}

export async function getCampaignPacing(campaignId: number, limit = 50): Promise<CampaignPacing> {
  const resp = await api.get<APIResponse<CampaignPacing>>(`/pacing/campaigns/${campaignId}`, {
    params: { limit },
  });
  return unwrap(resp.data);
}

export async function updateCampaignPacing(
  campaignId: number,
  body: PacingSettingsInput,
): Promise<CampaignPacing> {
  const resp = await api.put<APIResponse<CampaignPacing>>(`/pacing/campaigns/${campaignId}`, body);
  return unwrap(resp.data);
}
